import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Server, Layers, Network, X } from "lucide-react";
import axios from "axios";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const typeIcon = { cluster: Server, namespace: Layers, deployment: Layers, pod: Layers, service: Network };
const typeColor = { cluster: "text-indigo-400", namespace: "text-purple-400", deployment: "text-blue-400", pod: "text-emerald-400", service: "text-amber-400" };
const typeRoute = { cluster: "/clusters", namespace: "/explorer", deployment: "/explorer", pod: "/explorer", service: "/explorer" };

const QUICK_ACTIONS = [
  { label: "Go to Dashboard", key: "dashboard", route: "/dashboard" },
  { label: "Open AI Assistant", key: "assistant", route: "/assistant" },
  { label: "View Topology Map", key: "topology", route: "/topology" },
  { label: "Cost Intelligence", key: "cost", route: "/cost" },
  { label: "View Incidents", key: "incidents", route: "/incidents" },
];

export default function CommandPalette({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) { setQuery(""); setResults([]); setSelected(0); setTimeout(() => inputRef.current?.focus(), 50); }
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/search?q=${encodeURIComponent(query)}`);
        setResults(data);
      } catch (_) { setResults([]); }
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [query]);

  const filteredActions = query
    ? QUICK_ACTIONS.filter((a) => a.label.toLowerCase().includes(query.toLowerCase())).map((a) => ({ ...a, type: "action" }))
    : QUICK_ACTIONS.map((a) => ({ ...a, type: "action" }));
  const items = query ? [...filteredActions, ...results] : filteredActions;

  const handleSelect = (item) => {
    if (item.route) navigate(item.route);
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setSelected((s) => Math.min(s + 1, items.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSelected((s) => Math.max(s - 1, 0)); }
    else if (e.key === "Enter") { if (items[selected]) handleSelect(items[selected]); }
    else if (e.key === "Escape") onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" onClick={onClose}>
      <div
        className="w-full max-w-xl mx-4 rounded-lg border border-slate-700/80 shadow-2xl overflow-hidden cmd-backdrop"
        style={{ background: "rgba(2, 6, 23, 0.85)", backdropFilter: "blur(20px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-800/80">
          <Search size={16} className="text-slate-500 flex-shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelected(0); }}
            onKeyDown={handleKey}
            placeholder="Search clusters, pods, deployments..."
            className="flex-1 bg-transparent text-sm text-slate-50 placeholder-slate-500 outline-none"
          />
          {loading && <div className="w-3 h-3 rounded-full border border-slate-500 border-t-indigo-400 animate-spin" />}
          <button onClick={onClose} className="text-slate-500 hover:text-slate-300">
            <X size={14} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-72 overflow-y-auto">
          {!query && (
            <p className="px-4 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-600 font-semibold">Quick Actions</p>
          )}
          {items.length === 0 && query && (
            <p className="px-4 py-6 text-center text-sm text-slate-500">No results for "{query}"</p>
          )}
          {items.map((item, i) => {
            const Icon = item.type && typeIcon[item.type] ? typeIcon[item.type] : Search;
            return (
              <button
                key={item.id || item.key || i}
                onClick={() => handleSelect(item)}
                className={cn(
                  "flex items-center gap-3 w-full px-4 py-2.5 text-sm text-left transition-colors",
                  i === selected ? "bg-slate-800/80 text-slate-50" : "text-slate-300 hover:bg-slate-800/50"
                )}
              >
                <Icon size={14} className={cn(typeColor[item.type] || "text-slate-500", "flex-shrink-0")} />
                <span className="flex-1 truncate">{item.name || item.label}</span>
                {item.subtitle && <span className="text-xs text-slate-500 truncate max-w-[120px]">{item.subtitle}</span>}
                {item.status && (
                  <span className={cn("text-xs px-1.5 py-0.5 rounded border",
                    item.status === "healthy" || item.status === "Running" || item.status === "Active" ? "text-emerald-400 border-emerald-400/20 bg-emerald-400/10" :
                    item.status === "warning" || item.status === "Warning" ? "text-amber-400 border-amber-400/20 bg-amber-400/10" :
                    "text-rose-400 border-rose-400/20 bg-rose-400/10"
                  )}>
                    {item.status}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-slate-800/80 flex items-center gap-3 text-[10px] text-slate-600">
          <span><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span><kbd className="font-mono">↵</kbd> select</span>
          <span><kbd className="font-mono">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
